import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Camera, ImagePlus, X, Loader2, Trash2, Check, UserCheck, Search,
    ListPlus, Send, AlertTriangle, Banknote, Zap,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import BetDateSelector from './BetDateSelector';
import { downscaleImage } from '../utils/downscaleImage';
import ScanProgress from './ScanProgress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STATES = ['IL', 'MS', 'TN'];
const STATE_NAMES = { IL: 'Illinois', MS: 'Mississippi', TN: 'Tennessee' };
const MAX_BYTES = 6 * 1024 * 1024;
const QUEUE_KEY = 'betohalics:lottery-batch-queue';  // crash-proof persistence

const phoneDigits = (s) => (s || '').replace(/\D+/g, '');

/**
 * LotteryBatchScan — the fast "cashier with 20 tickets" tool.
 *
 * Snap/upload a photo → AI reads the bets AND any customer name/phone/account
 * written on the slip → we auto-match the account (auto-select on a confident
 * match, otherwise show candidates to pick). Review, optionally flag "paying
 * cash" (auto-loads the shortfall), then "Add to queue". Repeat for every
 * ticket, then "Submit all" fires the whole batch — each ticket attached to
 * its own customer.
 */
const LotteryBatchScan = ({ playersEndpoint = '/admin/users', onPlaced }) => {
    // Current in-progress ticket
    const [scanning, setScanning] = useState(false);
    const [previewSrc, setPreviewSrc] = useState('');
    const [bets, setBets] = useState([]);
    const [notes, setNotes] = useState('');
    const [scanId, setScanId] = useState(null);
    const [matched, setMatched] = useState(null);       // selected customer
    const [candidates, setCandidates] = useState([]);   // suggested matches
    const [cashTender, setCashTender] = useState(false);
    const [matching, setMatching] = useState(false);

    // Manual customer search (override)
    const [query, setQuery] = useState('');
    const [searchRes, setSearchRes] = useState([]);
    const [searching, setSearching] = useState(false);

    // The batch queue
    const [queue, setQueue] = useState([]);   // [{ id, customer, bets, cashTender }]
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(null);

    // Crash-proof: restore any queue left over from a crash/session timeout.
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
            if (Array.isArray(saved) && saved.length > 0) {
                setQueue(saved);
                toast.info(`Restored ${saved.length} unsent ticket${saved.length === 1 ? '' : 's'} from your last session.`);
            }
        } catch { /* ignore */ }
    }, []);

    // Persist the queue on every change so a bounce-to-lobby never loses work.
    useEffect(() => {
        try {
            if (queue.length > 0) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
            else localStorage.removeItem(QUEUE_KEY);
        } catch { /* ignore */ }
    }, [queue]);

    const cameraRef = useRef(null);
    const fileRef = useRef(null);

    const resetCurrent = () => {
        setBets([]); setNotes(''); setPreviewSrc(''); setScanId(null);
        setMatched(null); setCandidates([]); setCashTender(false); setQuery(''); setSearchRes([]);
    };

    // ── Customer auto-match from the OCR hint ────────────────────────────
    const autoMatch = useCallback(async (hint) => {
        if (!hint) { setCandidates([]); return; }
        setMatching(true);
        const phoneQ = phoneDigits(hint.phone || '');
        const nameQ = (hint.name || '').trim();
        const acctQ = (hint.account || '').trim();
        const pool = [];
        const seen = new Set();
        const queries = [phoneQ, nameQ, acctQ].filter(Boolean);
        try {
            for (const q of queries) {
                const { data } = await axios.get(`${API}${playersEndpoint}?q=${encodeURIComponent(q)}`);
                (Array.isArray(data) ? data : []).forEach(u => {
                    if (!seen.has(u.id)) { seen.add(u.id); pool.push(u); }
                });
            }
        } catch { /* non-fatal */ }

        const scored = pool.map(u => {
            let score = 0;
            const uphone = phoneDigits(u.phone);
            if (phoneQ && uphone) {
                if (uphone === phoneQ) score += 100;
                else if (uphone.slice(-7) === phoneQ.slice(-7)) score += 80;
            }
            if (acctQ && u.id === acctQ) score += 100;
            if (nameQ) {
                const un = (u.name || '').toLowerCase().trim();
                const hn = nameQ.toLowerCase();
                if (un && un === hn) score += 70;
                else if (un && (un.includes(hn) || hn.includes(un))) score += 40;
                else {
                    const ut = new Set(un.split(/\s+/).filter(Boolean));
                    const overlap = hn.split(/\s+/).filter(t => t && ut.has(t)).length;
                    if (overlap) score += 25 * overlap;
                }
            }
            return { u, score };
        }).filter(s => s.score > 0).sort((a, b) => b.score - a.score);

        setMatching(false);
        if (scored.length === 0) {
            setCandidates([]);
            toast.message('Ticket read — pick the customer to attach.');
            return;
        }
        const top = scored[0];
        const confident = top.score >= 80 && (scored.length === 1 || (top.score - (scored[1]?.score || 0)) >= 30);
        setCandidates(scored.slice(0, 5).map(s => s.u));
        if (confident) {
            setMatched(top.u);
            toast.success(`Auto-matched ${top.u.name || top.u.email} — confirm or change`);
        } else {
            toast.message('Possible customer(s) found — tap to confirm.');
        }
    }, [playersEndpoint]);

    // ── Scan a photo ─────────────────────────────────────────────────────
    const handleFile = useCallback(async (file) => {
        if (!file) return;
        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
            toast.error('Only JPG, PNG, or WEBP images supported.'); return;
        }
        if (file.size > MAX_BYTES) { toast.error('Image too large (max 6MB).'); return; }
        setScanning(true); setBets([]); setNotes(''); setMatched(null); setCandidates([]);
        const shrunk = await downscaleImage(file);
        if (!shrunk) { toast.error('Could not read that image.'); setScanning(false); return; }
        setPreviewSrc(shrunk.dataUrl);
        try {
            const { data } = await axios.post(`${API}/admin/lottery/scan`, {
                image_base64: shrunk.base64, mime_type: shrunk.mime,
            }, { timeout: 90000 });
            const seeded = (data?.bets || []).map((b, i) => ({ ...b, id: `${Date.now()}-${i}`, selected: true }));
            setBets(seeded);
            setNotes(data?.notes || '');
            setScanId(data?.scan_id || null);
            if (seeded.length === 0) toast.warning('No bets detected. Try another photo.');
            else toast.success(`Found ${seeded.length} bet${seeded.length === 1 ? '' : 's'}.`);
            if (data?.customer_hint) autoMatch(data.customer_hint);
            else toast.message('No name on slip — pick the customer to attach.');
        } catch (e) {
            // Robust: proxy/gateway errors return HTML (no JSON .detail) or no
            // response at all — show a clean retry message, never blank out.
            const detail = e.response?.data?.detail;
            const msg = (typeof detail === 'string' && detail)
                ? detail
                : (!e.response
                    ? 'Network hiccup — the scan didn’t go through. Check signal and tap Retake.'
                    : 'The scanner is busy right now — tap Retake to try again, or add the bets by hand.');
            toast.error(msg, { duration: 6000 });
        }
        setScanning(false);
    }, [autoMatch]);

    // ── Manual customer search (override) ─────────────────────────────────
    const runSearch = useCallback(async (q) => {
        if (!q.trim()) { setSearchRes([]); return; }
        setSearching(true);
        try {
            const { data } = await axios.get(`${API}${playersEndpoint}?q=${encodeURIComponent(q.trim())}`);
            setSearchRes(Array.isArray(data) ? data.slice(0, 8) : []);
        } catch { setSearchRes([]); }
        setSearching(false);
    }, [playersEndpoint]);

    const updateBet = (id, patch) => setBets(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
    const removeBet = (id) => setBets(prev => prev.filter(b => b.id !== id));
    const setDigit = (id, idx, val) => {
        const v = val === '' ? 0 : Math.max(0, Math.min(9, parseInt(val, 10) || 0));
        setBets(prev => prev.map(b => {
            if (b.id !== id) return b;
            const digits = [...b.digits]; digits[idx] = v; return { ...b, digits };
        }));
    };

    const validBets = bets.filter(b => b.selected && b.game_type !== 'Big 3');

    const addManualBet = () => setBets(prev => [...prev, {
        id: `m-${Date.now()}`,
        game_type: 'Pick 3',
        digits: [0, 0, 0],
        bet_type: 'straight',
        state: 'IL',
        drawing_time: 'evening',
        amount: 1,
        play_as_ways: false,
        ways: 1,
        dates: [],
        confidence: 0,
        selected: true,
    }]);

    const addToQueue = () => {
        if (!matched) { toast.error('Attach a customer first.'); return; }
        if (validBets.length === 0) { toast.error('No valid Pick 3/Pick 4 bets to add.'); return; }
        setQueue(prev => [...prev, {
            id: `q-${Date.now()}`,
            customer: matched,
            cashTender,
            bets: validBets.map(b => ({ ...b })),
        }]);
        toast.success(`Queued ${validBets.length} bet(s) for ${matched.name || matched.email}.`);
        resetCurrent();
    };

    const removeQueued = (id) => setQueue(prev => prev.filter(q => q.id !== id));

    const itemCost = (item) => item.bets.reduce((sum, b) => {
        const amt = Number(b.amount) || 0;
        const ways = b.play_as_ways ? Math.max(1, Number(b.ways) || 1) : 1;
        const days = Math.max(1, (b.dates?.length || 1));
        return sum + amt * ways * days;
    }, 0);

    const submitBatch = async () => {
        if (queue.length === 0) { toast.error('Queue is empty.'); return; }
        setSubmitting(true);
        let placed = 0; const errors = [];
        for (let i = 0; i < queue.length; i++) {
            const item = queue[i];
            setProgress({ done: i, total: queue.length, name: item.customer.name || item.customer.email });
            for (const b of item.bets) {
                try {
                    const pickType = b.game_type === 'Pick 4' ? 4 : 3;
                    const endpoint = b.play_as_ways
                        ? `${API}/admin/lottery/ticket-for-ways`
                        : `${API}/admin/lottery/ticket-for`;
                    await axios.post(endpoint, {
                        user_id: item.customer.id,
                        state: b.state,
                        pick_type: pickType,
                        numbers: b.digits,
                        bet_type: b.play_as_ways ? 'straight' : b.bet_type,
                        amount: Number(b.amount) || 1,
                        drawing_time: b.drawing_time,
                        cash_tender: !!item.cashTender,
                        ...(b.dates && b.dates.length > 0 ? { dates: b.dates } : {}),
                        ...(b.scan_id ? { scan_id: b.scan_id } : (scanId ? {} : {})),
                    });
                    placed += 1;
                } catch (e) {
                    errors.push(`${item.customer.name || 'player'} ${b.digits.join('-')}: ${e.response?.data?.detail || 'failed'}`);
                }
            }
        }
        setProgress(null);
        setSubmitting(false);
        if (placed > 0) {
            toast.success(`Placed ${placed} bet${placed === 1 ? '' : 's'} across ${queue.length} ticket(s)!`);
            setQueue([]);
            if (onPlaced) onPlaced();
        }
        if (errors.length > 0) toast.error(`${errors.length} failed: ${errors.slice(0, 2).join(' · ')}`);
    };

    const queueTotal = queue.reduce((s, it) => s + itemCost(it), 0);

    return (
        <div className="space-y-4" data-testid="lottery-batch-scan">
            <div className="card-dark p-4" style={{ borderLeft: '3px solid #39FF14' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-5 h-5 text-[#39FF14]" />
                    <h3 className="text-lg font-bold text-white">Batch Scan &amp; Log Tickets</h3>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-3">
                    Snap each customer's slip — the scan reads the bets <span className="text-white font-semibold">and</span> the name/phone on the ticket to auto-attach the right account. Queue them up, then submit all at once.
                </p>

                {/* File inputs are always mounted so Camera/Upload AND the
                    "Retake" button (shown in review mode) can trigger them. */}
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} className="hidden" data-testid="batch-camera-input" />
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} className="hidden" data-testid="batch-file-input" />

                {/* SCAN AREA — only show the upload prompt when no photo is loaded */}
                {!previewSrc && !scanning && (
                    <div className="rounded-xl border-2 border-dashed border-white/15 py-8 text-center bg-[#0a0a0a]">
                        <ImagePlus className="w-9 h-9 text-[#A0AAB2] mx-auto mb-2" />
                        <p className="text-xs text-[#A0AAB2] mb-3">Snap or upload the customer's handwritten slip</p>
                        <div className="flex gap-2 justify-center">
                            <Button onClick={() => cameraRef.current?.click()} className="bg-[#FF007F] text-white font-bold h-10" data-testid="batch-camera-btn">
                                <Camera className="w-4 h-4 mr-1" /> Camera
                            </Button>
                            <Button variant="outline" onClick={() => fileRef.current?.click()} className="border-white/20 text-white h-10" data-testid="batch-upload-btn">
                                <ImagePlus className="w-4 h-4 mr-1" /> Upload
                            </Button>
                        </div>
                    </div>
                )}

                {scanning && (
                    <div className="py-2" data-testid="batch-scanning">
                        <ScanProgress label="Reading ticket & customer…" />
                    </div>
                )}

                {/* REVIEW CURRENT TICKET — stays put once a photo is loaded, even
                    if the scan read 0 bets (so the panel never "flashes away") */}
                {!scanning && previewSrc && (
                    <div className="space-y-3">
                        {previewSrc && (
                            <div className="relative rounded-lg overflow-hidden border border-white/10">
                                <img src={previewSrc} alt="slip" className="w-full max-h-36 object-contain bg-[#0a0a0a]" />
                                <button type="button" onClick={resetCurrent} className="absolute top-2 right-2 bg-black/60 hover:bg-black text-white rounded-full p-1" aria-label="Discard">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* CUSTOMER MATCH */}
                        <div className="rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/5 p-3" data-testid="batch-customer-block">
                            <div className="flex items-center gap-2 mb-2">
                                <UserCheck className="w-4 h-4 text-[#39FF14]" />
                                <span className="text-[11px] uppercase tracking-widest text-[#39FF14] font-black">Customer</span>
                                {matching && <Loader2 className="w-3 h-3 animate-spin text-[#A0AAB2]" />}
                            </div>
                            {matched ? (
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="min-w-0">
                                        <p className="text-white font-black text-sm truncate" data-testid="batch-matched-name">{matched.name || matched.email}</p>
                                        <p className="text-[10px] text-[#A0AAB2] truncate">
                                            {matched.phone || matched.email} ·
                                            <span className="text-[#FFD700] font-mono ml-1">
                                                ${((Number(matched.credits) || 0) + (Number(matched.bonus_credits) || 0)).toFixed(2)} playable
                                            </span>
                                        </p>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => { setMatched(null); }} className="border-white/10 text-white text-xs h-7" data-testid="batch-change-customer">
                                        Change
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {candidates.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2" data-testid="batch-candidates">
                                            {candidates.map(c => (
                                                <button key={c.id} type="button" onClick={() => setMatched(c)}
                                                    className="inline-flex items-center gap-1 bg-[#7C3AED]/15 border border-[#7C3AED]/40 rounded-full text-[11px] text-white px-2 py-1 hover:bg-[#7C3AED]/30"
                                                    data-testid={`batch-candidate-${c.id}`}>
                                                    <span className="font-bold">{c.name || c.email}</span>
                                                    <span className="text-[#A0AAB2]">{c.phone || ''}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <div className="relative">
                                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                                        <Input
                                            value={query}
                                            onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
                                            placeholder="Search name / phone / email…"
                                            className="pl-8 h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                            data-testid="batch-customer-search"
                                        />
                                    </div>
                                    {searching && <p className="text-[10px] text-[#A0AAB2] mt-1">Searching…</p>}
                                    {searchRes.length > 0 && (
                                        <div className="mt-1 rounded-lg border border-white/10 bg-[#0a0a0a] max-h-40 overflow-y-auto">
                                            {searchRes.map(u => (
                                                <button key={u.id} type="button" onClick={() => { setMatched(u); setQuery(''); setSearchRes([]); }}
                                                    className="w-full text-left px-3 py-1.5 border-b border-white/5 last:border-0 hover:bg-white/5"
                                                    data-testid={`batch-search-row-${u.id}`}>
                                                    <p className="text-xs font-bold text-white truncate">{u.name || u.email}</p>
                                                    <p className="text-[10px] text-[#A0AAB2] truncate">{u.phone || u.email}</p>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {notes && <p className="text-[10px] text-[#A0AAB2] italic">AI notes: {notes}</p>}

                        {/* NO BETS READ — keep preview + customer, offer retake / manual add */}
                        {bets.length === 0 && (
                            <div className="rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/10 p-3 text-center" data-testid="batch-no-bets">
                                <AlertTriangle className="w-6 h-6 text-[#FFD700] mx-auto mb-1" />
                                <p className="text-sm text-white font-bold">Couldn't read any bets on this photo</p>
                                <p className="text-[11px] text-[#A0AAB2] mt-0.5 mb-2">Retake it sharper &amp; well-lit, or add the lines by hand — the customer is still attached.</p>
                                <div className="flex gap-2 justify-center">
                                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="border-white/20 text-white" data-testid="batch-retake-btn">
                                        <ImagePlus className="w-4 h-4 mr-1" /> Retake / re-upload
                                    </Button>
                                    <Button size="sm" onClick={addManualBet} className="bg-[#39FF14] text-black font-bold" data-testid="batch-add-manual-btn">
                                        <ListPlus className="w-4 h-4 mr-1" /> Add bet manually
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* BETS */}
                        <div className="space-y-2">
                            <AnimatePresence initial={false}>
                                {bets.map((b, idx) => (
                                    <motion.div key={b.id} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                                        className={`rounded-lg border p-2 ${b.selected ? 'bg-[#0e0e0e] border-white/15' : 'bg-[#0e0e0e]/50 border-white/5 opacity-60'}`}
                                        data-testid={`batch-bet-${idx}`}>
                                        <div className="flex items-start gap-2">
                                            <input type="checkbox" checked={b.selected} onChange={(e) => updateBet(b.id, { selected: e.target.checked })} className="w-4 h-4 mt-1 accent-[#39FF14]" />
                                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-1">
                                                <select value={b.game_type === 'Big 3' ? 'Pick 3' : b.game_type}
                                                    onChange={(e) => {
                                                        const gt = e.target.value; const need = gt === 'Pick 4' ? 4 : 3;
                                                        const digits = b.digits.slice(0, need); while (digits.length < need) digits.push(0);
                                                        updateBet(b.id, { game_type: gt, digits });
                                                    }}
                                                    className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1">
                                                    <option value="Pick 3">Pick 3</option>
                                                    <option value="Pick 4">Pick 4</option>
                                                </select>
                                                <select value={b.state} onChange={(e) => updateBet(b.id, { state: e.target.value })} className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1">
                                                    {STATES.map(s => <option key={s} value={s}>{STATE_NAMES[s]}</option>)}
                                                </select>
                                                <select value={b.play_as_ways ? 'ways' : b.bet_type}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        if (v === 'ways') updateBet(b.id, { play_as_ways: true, bet_type: 'box' });
                                                        else updateBet(b.id, { play_as_ways: false, bet_type: v });
                                                    }}
                                                    className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1">
                                                    <option value="straight">Straight</option>
                                                    <option value="box">Box</option>
                                                    {(b.ways || 1) > 1 && <option value="ways">Combo ({b.ways}-way)</option>}
                                                </select>
                                                <div className="relative">
                                                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#FFD700] text-xs font-bold pointer-events-none">$</span>
                                                    <input type="number" step="0.5" min="0.5" value={b.amount} onChange={(e) => updateBet(b.id, { amount: e.target.value })}
                                                        className="h-7 pl-4 bg-[#0a0a0a] border border-[#FFD700]/30 rounded text-[#FFD700] text-xs font-bold px-1 w-full" data-testid={`batch-amount-${idx}`} />
                                                </div>
                                                <select value={b.drawing_time} onChange={(e) => updateBet(b.id, { drawing_time: e.target.value })} className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1">
                                                    <option value="midday">Midday</option>
                                                    <option value="evening">Evening</option>
                                                </select>
                                            </div>
                                            <button type="button" onClick={() => removeBet(b.id)} className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded" aria-label="Remove">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        <div className="flex gap-1 mt-2 ml-6 items-end">
                                            {b.digits.map((d, i) => (
                                                <input key={i} type="number" min="0" max="9" value={d} onChange={(e) => setDigit(b.id, i, e.target.value)}
                                                    className="w-9 h-9 text-center text-base font-bold bg-[#0a0a0a] border border-white/15 rounded text-white" data-testid={`batch-digit-${idx}-${i}`} />
                                            ))}
                                            <span className={`ml-auto self-end text-[10px] font-mono ${b.confidence > 0.75 ? 'text-[#39FF14]' : b.confidence > 0.5 ? 'text-[#FFD700]' : 'text-[#A0AAB2]'}`}>
                                                {Math.round((b.confidence || 0) * 100)}%
                                            </span>
                                        </div>
                                        <div className="mt-2 pt-2 border-t border-white/5">
                                            <BetDateSelector dates={b.dates} onChange={(d) => updateBet(b.id, { dates: d })} testIdPrefix={`batch-date-${idx}`} />
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                            {bets.length > 0 && (
                                <button type="button" onClick={addManualBet}
                                    className="w-full mt-1 h-8 rounded-lg border border-dashed border-white/20 text-[11px] font-bold text-[#39FF14] hover:bg-[#39FF14]/10 flex items-center justify-center gap-1"
                                    data-testid="batch-add-line-btn">
                                    <ListPlus className="w-3.5 h-3.5" /> Add another bet
                                </button>
                            )}
                        </div>

                        {/* CASH TENDER TOGGLE */}
                        <label className="flex items-center gap-2 rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/5 p-2.5 cursor-pointer" data-testid="batch-cash-toggle">
                            <input type="checkbox" checked={cashTender} onChange={(e) => setCashTender(e.target.checked)} className="w-4 h-4 accent-[#FFD700]" />
                            <Banknote className="w-4 h-4 text-[#FFD700]" />
                            <span className="text-xs text-white font-semibold">Customer is paying cash — auto-load their account if short</span>
                        </label>

                        <div className="flex gap-2">
                            <Button variant="outline" onClick={resetCurrent} className="flex-1 border-white/15 text-white" data-testid="batch-discard-btn">
                                <X className="w-4 h-4 mr-1" /> Discard
                            </Button>
                            <Button onClick={addToQueue} disabled={!matched || validBets.length === 0}
                                className="flex-1 font-black text-black bg-[#39FF14] hover:bg-[#39FF14]/90" data-testid="batch-add-btn">
                                <ListPlus className="w-4 h-4 mr-1" /> Add to queue
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* THE QUEUE */}
            <div className="card-dark p-4">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                        <ListPlus className="w-4 h-4 text-[#39FF14]" /> Queue
                        <span className="text-[#A0AAB2] font-mono">({queue.length})</span>
                    </h4>
                    {queue.length > 0 && (
                        <span className="text-xs text-[#A0AAB2]">Total <span className="text-[#FFD700] font-mono font-bold">${queueTotal.toFixed(2)}</span></span>
                    )}
                </div>
                {queue.length === 0 ? (
                    <p className="text-[11px] text-[#A0AAB2] text-center py-4">No tickets queued yet. Scan a slip above to start.</p>
                ) : (
                    <div className="space-y-1.5" data-testid="batch-queue-list">
                        {queue.map((it) => (
                            <div key={it.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#0a0a0a] px-3 py-2" data-testid={`batch-queue-${it.id}`}>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-white truncate">
                                        {it.customer.name || it.customer.email}
                                        {it.cashTender && <span className="ml-2 text-[9px] bg-[#FFD700]/15 text-[#FFD700] px-1.5 py-0.5 rounded font-black uppercase">Cash</span>}
                                    </p>
                                    <p className="text-[10px] text-[#A0AAB2] truncate">
                                        {it.bets.map(b => `${b.state} ${b.digits.join('')} ${b.play_as_ways ? `C${b.ways}` : (b.bet_type === 'box' ? 'BX' : 'ST')}`).join(' · ')}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-xs font-mono text-[#FFD700]">${itemCost(it).toFixed(2)}</span>
                                    <button type="button" onClick={() => removeQueued(it.id)} className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded" aria-label="Remove" data-testid={`batch-queue-remove-${it.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <Button onClick={submitBatch} disabled={submitting}
                            className="w-full mt-2 h-11 font-black text-white" style={{ background: 'linear-gradient(135deg, #FF007F, #7C3AED)' }} data-testid="batch-submit-all-btn">
                            {submitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {progress ? `Placing ${progress.done + 1}/${progress.total}…` : 'Submitting…'}</>
                            ) : (
                                <><Send className="w-4 h-4 mr-2" /> Submit all {queue.length} ticket{queue.length === 1 ? '' : 's'} · ${queueTotal.toFixed(2)}</>
                            )}
                        </Button>
                        <div className="text-[10px] text-[#666] mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Each ticket debits its customer's balance and is logged to audit.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LotteryBatchScan;
